import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import { 
  FiPlus, 
  FiEdit2, 
  FiTrash2, 
  FiLoader, 
  FiEye,
  FiSave,
  FiX,
  FiSearch,
  FiLink,
  FiChevronLeft,
  FiChevronRight,
  FiExternalLink
} from "react-icons/fi";
import { 
  FaFacebook, 
  FaTwitter, 
  FaInstagram, 
  FaLinkedin, 
  FaYoutube, 
  FaSnapchat, 
  FaPinterest, 
  FaReddit,
  FaGithub,
  FaGlobe
} from "react-icons/fa";

const SocialContacts = () => {
  const [loading, setLoading] = useState(true);
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  
  // Form states
  const [newContacts, setNewContacts] = useState([{ name: "", url: "" }]);
  const [editingContact, setEditingContact] = useState({ id: null, name: "", url: "" });
  
  // Loading states
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const API_URL = "https://api.dreamtoday.in/api/admin";

  // Fetch all social contacts
  const fetchContacts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/allsocial-contact`, {
        withCredentials: true
      });
      
      console.log("Contacts response:", response.data);
      
      if (response.data.success) {
        setContacts(response.data.contacts || []);
        setFilteredContacts(response.data.contacts || []);
      }
    } catch (error) {
      console.error("Error fetching contacts:", error);
      toast.error("Failed to fetch contacts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  // Search and filter
  useEffect(() => {
    if (searchTerm) {
      const filtered = contacts.filter(contact => 
        contact.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contact.url?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredContacts(filtered);
    } else {
      setFilteredContacts(contacts);
    }
    setCurrentPage(1);
  }, [searchTerm, contacts]);

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredContacts.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredContacts.length / itemsPerPage);

  // Handle create contacts
  const handleCreateContacts = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      // Filter out empty entries
      const validContacts = newContacts.filter(c => 
        c.name.trim() !== "" && c.url.trim() !== ""
      );
      
      if (validContacts.length === 0) {
        toast.error("At least one contact with name and URL is required");
        setCreating(false);
        return;
      }

      const response = await axios.post(
        `${API_URL}/addsocial-contact`,
        validContacts,  // Send array directly
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success(response.data.message);
        setShowCreateModal(false);
        setNewContacts([{ name: "", url: "" }]);
        fetchContacts();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to create contacts");
    } finally {
      setCreating(false);
    }
  };

  // Handle update contact
  const handleUpdateContact = async (e) => {
    e.preventDefault();
    setUpdating(true);

    try {
      if (!editingContact.name.trim() || !editingContact.url.trim()) {
        toast.error("Name and URL are required");
        setUpdating(false);
        return;
      }

      const response = await axios.put(
        `${API_URL}/updatesocial-contact/${editingContact.id}`,
        {
          name: editingContact.name,
          url: editingContact.url
        },
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success("Contact updated successfully!");
        setShowEditModal(false);
        setEditingContact({ id: null, name: "", url: "" });
        fetchContacts();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update contact");
    } finally {
      setUpdating(false);
    }
  };

  // Handle delete contact
  const handleDeleteContact = async () => {
    if (!selectedContact) return;
    
    setDeleting(true);
    try {
      const response = await axios.delete(
        `${API_URL}/deletesocial-contact/${selectedContact._id}`,
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success("Contact deleted successfully!");
        setShowDeleteModal(false);
        setSelectedContact(null);
        fetchContacts();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete contact");
    } finally {
      setDeleting(false);
    }
  };

  // Form handlers
  const addContactField = () => setNewContacts([...newContacts, { name: "", url: "" }]);
  const removeContactField = (index) => {
    if (newContacts.length > 1) {
      setNewContacts(newContacts.filter((_, i) => i !== index));
    }
  };
  const updateContactField = (index, field, value) => {
    const updated = [...newContacts];
    updated[index][field] = value;
    setNewContacts(updated);
  };

  // Get social icon based on name
  const getSocialIcon = (name) => {
    const lowerName = name.toLowerCase();
    if (lowerName.includes('facebook')) return <FaFacebook className="text-blue-600" />;
    if (lowerName.includes('twitter')) return <FaTwitter className="text-blue-400" />;
    if (lowerName.includes('instagram')) return <FaInstagram className="text-pink-600" />;
    if (lowerName.includes('linkedin')) return <FaLinkedin className="text-blue-700" />;
    if (lowerName.includes('youtube')) return <FaYoutube className="text-red-600" />;
    if (lowerName.includes('snapchat')) return <FaSnapchat className="text-yellow-600" />;
    if (lowerName.includes('pinterest')) return <FaPinterest className="text-red-700" />;
    if (lowerName.includes('reddit')) return <FaReddit className="text-orange-600" />;
    if (lowerName.includes('github')) return <FaGithub className="text-gray-800" />;
    return <FaGlobe className="text-gray-600" />;
  };

  // Format date
  const formatDate = (date) => new Date(date).toLocaleDateString();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <FiLoader className="animate-spin text-4xl text-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <FiLink className="text-blue-500" />
              Social Contacts
            </h1>
            <p className="text-gray-600 mt-1">Total {filteredContacts.length} contacts found</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <FiPlus /> Add Contacts
          </button>
        </div>

        {/* Search */}
        <div className="bg-white rounded-lg shadow mb-6 p-4">
          <div className="relative">
            <FiSearch className="absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or URL..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Table */}
        {currentItems.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <FiLink className="text-6xl text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Contacts Found</h3>
            <p className="text-gray-500 mb-6">Get started by adding your first social contact</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Add Contact
            </button>
          </div>
        ) : (
          <>
            <div className="bg-white rounded-lg shadow overflow-hidden mb-6">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold">S.No</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Platform</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">URL</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Created</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {currentItems.map((contact, idx) => (
                      <tr key={contact._id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {indexOfFirstItem + idx + 1}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                              {getSocialIcon(contact.name)}
                            </div>
                            <div className="font-medium">{contact.name}</div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <a 
                            href={contact.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                          >
                            {contact.url.length > 30 ? contact.url.substring(0, 30) + '...' : contact.url}
                            <FiExternalLink size={12} />
                          </a>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {formatDate(contact.createdAt)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <button
                              onClick={() => {
                                setSelectedContact(contact);
                                setShowViewModal(true);
                              }}
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                              title="View"
                            >
                              <FiEye />
                            </button>
                            <button
                              onClick={() => {
                                setEditingContact({
                                  id: contact._id,
                                  name: contact.name,
                                  url: contact.url
                                });
                                setShowEditModal(true);
                              }}
                              className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                              title="Edit"
                            >
                              <FiEdit2 />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedContact(contact);
                                setShowDeleteModal(true);
                              }}
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                              title="Delete"
                            >
                              <FiTrash2 />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredContacts.length)} of {filteredContacts.length}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                    disabled={currentPage === 1}
                    className="p-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50"
                  >
                    <FiChevronLeft />
                  </button>
                  <span className="px-4 py-2 bg-blue-600 text-white rounded-lg">
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="p-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50"
                  >
                    <FiChevronRight />
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Create Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold">Add Social Contacts</h2>
                  <button onClick={() => setShowCreateModal(false)} className="p-1 hover:bg-gray-100 rounded">
                    <FiX />
                  </button>
                </div>

                <form onSubmit={handleCreateContacts} className="space-y-4">
                  {newContacts.map((contact, index) => (
                    <div key={index} className="border p-4 rounded-lg relative">
                      {newContacts.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeContactField(index)}
                          className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full"
                        >
                          <FiX size={14} />
                        </button>
                      )}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Platform Name</label>
                          <input
                            type="text"
                            placeholder="e.g., Facebook"
                            value={contact.name}
                            onChange={(e) => updateContactField(index, "name", e.target.value)}
                            className="w-full p-2 border rounded"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">URL</label>
                          <input
                            type="url"
                            placeholder="https://..."
                            value={contact.url}
                            onChange={(e) => updateContactField(index, "url", e.target.value)}
                            className="w-full p-2 border rounded"
                            required
                          />
                        </div>
                      </div>
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={addContactField}
                    className="w-full py-2 border-2 border-dashed rounded-lg text-gray-600 hover:text-blue-600"
                  >
                    + Add Another Contact
                  </button>

                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setShowCreateModal(false)}
                      className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={creating}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      {creating ? "Creating..." : "Create"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {showEditModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold">Edit Contact</h2>
                  <button onClick={() => setShowEditModal(false)} className="p-1 hover:bg-gray-100 rounded">
                    <FiX />
                  </button>
                </div>

                <form onSubmit={handleUpdateContact} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Platform Name</label>
                    <input
                      type="text"
                      value={editingContact.name}
                      onChange={(e) => setEditingContact({...editingContact, name: e.target.value})}
                      className="w-full p-2 border rounded"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">URL</label>
                    <input
                      type="url"
                      value={editingContact.url}
                      onChange={(e) => setEditingContact({...editingContact, url: e.target.value})}
                      className="w-full p-2 border rounded"
                      required
                    />
                  </div>

                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setShowEditModal(false)}
                      className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={updating}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                    >
                      {updating ? "Updating..." : "Update"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* View Modal */}
        {showViewModal && selectedContact && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold">Contact Details</h2>
                  <button onClick={() => setShowViewModal(false)} className="p-1 hover:bg-gray-100 rounded">
                    <FiX />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center gap-4 p-3 bg-gray-50 rounded">
                    <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center text-2xl">
                      {getSocialIcon(selectedContact.name)}
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Platform</p>
                      <p className="font-semibold">{selectedContact.name}</p>
                    </div>
                  </div>

                  <div className="p-3 bg-gray-50 rounded">
                    <p className="text-sm text-gray-500 mb-1">URL</p>
                    <a 
                      href={selectedContact.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline break-all"
                    >
                      {selectedContact.url}
                    </a>
                  </div>

                  <div className="p-3 bg-gray-50 rounded">
                    <p className="text-sm text-gray-500 mb-1">Contact ID</p>
                    <p className="text-sm font-mono break-all bg-white p-2 rounded border">
                      {selectedContact._id}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-gray-50 rounded">
                      <p className="text-xs text-gray-500">Created</p>
                      <p className="text-sm">{formatDate(selectedContact.createdAt)}</p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-2 mt-6">
                  <button
                    onClick={() => {
                      setShowViewModal(false);
                      setEditingContact({
                        id: selectedContact._id,
                        name: selectedContact.name,
                        url: selectedContact.url
                      });
                      setShowEditModal(true);
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => setShowViewModal(false)}
                    className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Modal */}
        {showDeleteModal && selectedContact && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <div className="text-center">
                <FiTrash2 className="text-5xl text-red-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2">Delete Contact</h3>
                <p className="text-gray-600 mb-6">
                  Are you sure you want to delete <span className="font-semibold">{selectedContact.name}</span>?
                </p>
                
                <div className="flex justify-center gap-3">
                  <button
                    onClick={() => setShowDeleteModal(false)}
                    className="px-6 py-2 border rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteContact}
                    disabled={deleting}
                    className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    {deleting ? <FiLoader className="animate-spin" /> : <FiTrash2 />}
                    {deleting ? "Deleting..." : "Delete"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SocialContacts;